package com.klantenstop.web.pages.manageCompany.edit

import com.klantenstop.web.data.models.domain.Category
import com.klantenstop.web.data.models.domain.Company
import com.klantenstop.web.pages.manageCompany.components.AddressValidationState
import com.klantenstop.web.utils.MVI
import com.klantenstop.web.utils.websiteRegex

interface EditCompanyContract : MVI<EditCompanyContract.State, EditCompanyContract.Intent, EditCompanyContract.Effect> {

    data class State(
        val originalCompany: Company? = null,
        val categories: List<Category> = emptyList(),
        val companyName: String = "",
        val companyDescription: String = "",
        val phoneNumber: String = "",
        val emailAddress: String = "",
        val website: String = "",
        // Address fields
        val streetName: String = "",
        val streetNumber: String = "",
        val zipCode: String = "",
        val city: String = "",
        val country: String = "",
        val isValidatingAddress: Boolean = false,
        val addressValidationResult: AddressValidationState? = null,
        val addressLatitude: Double? = null,
        val addressLongitude: Double? = null,
        // End address fields
        val selectedCategory: Category? = null,
        val hasAttemptedSave: Boolean = false,
        val callOnRoute: Boolean = false
    ) {
        private val companyNameChanged = companyName != originalCompany?.name
        private val phoneNumberChanged = phoneNumber != originalCompany?.phoneNumber
        private val emailAddressChanged = emailAddress != originalCompany?.emailAddress
        private val websiteChanged = website != originalCompany?.website?.href
        private val companyDescriptionChanged = companyDescription != originalCompany?.description
        private val selectedCategoryChanged = selectedCategory != originalCompany?.categories?.firstOrNull()
        private val addressChanged = streetName != originalCompany?.address?.streetName ||
                streetNumber != originalCompany?.address?.streetNumber ||
                zipCode != originalCompany?.address?.postalCode ||
                city != originalCompany?.address?.city ||
                country != originalCompany?.address?.country

        val isCompanyNameValid: Boolean = companyName.isNotBlank()
        val isPhoneNumberValid: Boolean = true // Optional field
        val isEmailAddressValid: Boolean = emailAddress.isBlank() || emailAddress.contains("@")
        val isWebsiteValid: Boolean = website.isBlank() || website.matches(websiteRegex)
        val isCategoryValid: Boolean = selectedCategory != null
        val isAddressComplete: Boolean = streetName.isNotBlank() && streetNumber.isNotBlank() &&
                zipCode.isNotBlank() && city.isNotBlank() && country.isNotBlank()
        val isAddressValidated: Boolean = !addressChanged || addressValidationResult is AddressValidationState.Success

        val hasChanged: Boolean = companyNameChanged ||
                phoneNumberChanged ||
                emailAddressChanged ||
                websiteChanged ||
                companyDescriptionChanged ||
                selectedCategoryChanged ||
                addressChanged
    }

    sealed class Intent {
        data object LoadInitialData : Intent()
        data class UpdateCompanyName(val name: String) : Intent()
        data class UpdateDescription(val description: String) : Intent()
        data class UpdatePhoneNumber(val phoneNumber: String) : Intent()
        data class UpdateEmailAddress(val emailAddress: String) : Intent()
        data class UpdateWebsite(val website: String) : Intent()
        data class UpdateCategory(val companyCategory: Category?) : Intent()

        // Address intents
        data class UpdateStreetName(val streetName: String) : Intent()
        data class UpdateStreetNumber(val streetNumber: String) : Intent()
        data class UpdateZipCode(val zipCode: String) : Intent()
        data class UpdateCity(val city: String) : Intent()
        data class UpdateCountry(val country: String) : Intent()
        data object ValidateAddress : Intent()

        data object Save : Intent()
        data object Cancel : Intent()
    }

    sealed class Effect {
        data class NavigateBack(val companyIdentifier: String? = null) : Effect()
    }
}
